import { ELeague, getLeagueOrder } from '../../enums';

import { GameStatsDto } from './basic.user.entity';

export const LEAGUE_STAKE_CAPS_CENTS: Record<ELeague, number> = {
  [ELeague.IRON]: 1500,
  [ELeague.BRONZE]: 5000,
  [ELeague.SILVER]: 20000,
  [ELeague.GOLD]: 100000,
  [ELeague.PLATINUM]: 500000,
  [ELeague.DIAMOND]: 2000000,
};

export const DEFAULT_RATING_K_FACTOR = 80;
export const EQUAL_OPPONENT_RATING_DELTA = DEFAULT_RATING_K_FACTOR / 2;

const IRON_LEAGUE_MAX_RATING_EXCLUSIVE = 1100;
const BRONZE_LEAGUE_MAX_RATING_EXCLUSIVE = 1300;
const SILVER_LEAGUE_MAX_RATING_EXCLUSIVE = 1400;
const GOLD_LEAGUE_MAX_RATING_EXCLUSIVE = 1450;
const PLATINUM_LEAGUE_MAX_RATING_EXCLUSIVE = 1500;

export const LEAGUE_MAX_RATING_EXCLUSIVE: Readonly<
  Record<ELeague, number | null>
> = {
  [ELeague.IRON]: IRON_LEAGUE_MAX_RATING_EXCLUSIVE,
  [ELeague.BRONZE]: BRONZE_LEAGUE_MAX_RATING_EXCLUSIVE,
  [ELeague.SILVER]: SILVER_LEAGUE_MAX_RATING_EXCLUSIVE,
  [ELeague.GOLD]: GOLD_LEAGUE_MAX_RATING_EXCLUSIVE,
  [ELeague.PLATINUM]: PLATINUM_LEAGUE_MAX_RATING_EXCLUSIVE,
  [ELeague.DIAMOND]: null,
};

/**
 * Get the user's rating for a specific game.
 * Returns the default value if no rating is set for the game.
 *
 * @param gameStats - user stats
 * @param gameId - game ID
 * @param defaultRating - default rating (1000 by default)
 * @returns user rating for the game
 */
export function getRatingForGame(
  gameStats: GameStatsDto | undefined | null,
  gameId: number,
  defaultRating = 1000,
): number {
  if (!gameStats?.ratingsPerGame) {
    return defaultRating;
  }

  return gameStats.ratingsPerGame[gameId] ?? defaultRating;
}

export function getLeagueForRating(rating: number): ELeague {
  if (rating < IRON_LEAGUE_MAX_RATING_EXCLUSIVE) {
    return ELeague.IRON;
  }

  if (rating < BRONZE_LEAGUE_MAX_RATING_EXCLUSIVE) {
    return ELeague.BRONZE;
  }

  if (rating < SILVER_LEAGUE_MAX_RATING_EXCLUSIVE) {
    return ELeague.SILVER;
  }

  if (rating < GOLD_LEAGUE_MAX_RATING_EXCLUSIVE) {
    return ELeague.GOLD;
  }

  if (rating < PLATINUM_LEAGUE_MAX_RATING_EXCLUSIVE) {
    return ELeague.PLATINUM;
  }

  return ELeague.DIAMOND;
}

export function getMinLeagueForGame(
  gameStats: GameStatsDto | undefined | null,
  gameId: number,
): ELeague | undefined {
  return gameStats?.minLeaguePerGame?.[String(gameId)];
}

/**
 * League with the safe-league mechanic applied.
 * The user cannot drop below a previously reached league (minLeaguePerGame).
 */
export function getSafeLeagueForGame(
  gameStats: GameStatsDto | undefined | null,
  gameId: number,
  defaultRating = 1000,
): ELeague {
  const rating = getRatingForGame(gameStats, gameId, defaultRating);
  const ratingLeague = getLeagueForRating(rating);
  const minLeague = getMinLeagueForGame(gameStats, gameId);
  if (!minLeague) {
    return ratingLeague;
  }

  return getLeagueOrder(minLeague) > getLeagueOrder(ratingLeague)
    ? minLeague
    : ratingLeague;
}

/**
 * Number of consecutive wins against equally rated opponents required
 * to advance to the next league (rounded up).
 */
export function getWinStreakRequiredForNextLeague(
  rating: number,
  kFactor = DEFAULT_RATING_K_FACTOR,
): number | null {
  if (!Number.isFinite(rating)) {
    throw new Error(`Invalid rating: ${String(rating)}`);
  }
  if (!Number.isFinite(kFactor) || kFactor <= 0) {
    throw new Error(`Invalid kFactor: ${String(kFactor)}`);
  }

  const currentLeague = getLeagueForRating(rating);
  const nextLeagueRating = LEAGUE_MAX_RATING_EXCLUSIVE[currentLeague];
  if (nextLeagueRating === null) {
    return null;
  }

  const ratingToNextLeague = nextLeagueRating - rating;
  if (ratingToNextLeague <= 0) {
    return 0;
  }

  const ratingGainPerWin = kFactor / 2;
  return Math.ceil(ratingToNextLeague / ratingGainPerWin);
}

export function getStakeCapCentsForLeague(league: ELeague): number {
  return LEAGUE_STAKE_CAPS_CENTS[league];
}

/**
 * Set the user's rating for a specific game.
 *
 * @param gameStats - user stats
 * @param gameId - game ID
 * @param rating - new rating
 * @returns updated stats
 */
export function setRatingForGame(
  gameStats: GameStatsDto | undefined | null,
  gameId: number,
  rating: number,
): GameStatsDto {
  const stats = gameStats || {};
  if (!stats.ratingsPerGame) {
    stats.ratingsPerGame = {};
  }
  stats.ratingsPerGame[gameId] = rating;
  return stats;
}

/**
 * Get all user ratings by game.
 *
 * @param gameStats - user stats
 * @returns Record<gameId, rating> or an empty object
 */
export function getAllRatings(
  gameStats: GameStatsDto | undefined | null,
): Record<number, number> {
  return gameStats?.ratingsPerGame || {};
}

/**
 * Increment the games-played count for a specific game.
 * Used for per-game statistics.
 *
 * @param gameStats - user stats
 * @param gameId - game ID
 * @returns updated stats
 */
export function incrementGamesPlayed(
  gameStats: GameStatsDto | undefined | null,
  gameId: number,
): GameStatsDto {
  const stats = gameStats || {};

  // Total games counter
  stats.gamesPlayed = (stats.gamesPlayed || 0) + 1;

  // Per-game games counter (can be extended)
  if (!stats.gamesPerGame) {
    stats.gamesPerGame = {};
  }
  stats.gamesPerGame[gameId] = (stats.gamesPerGame[gameId] || 0) + 1;

  return stats;
}
